import { useAuth } from "@/context/AuthContext";
import { LogOut, Globe, ShieldCheck, CreditCard, RefreshCw, Bell, Lock, Trash2, AlertTriangle } from "lucide-react";
import { useNavigate, Link } from "react-router-dom";
import { tr } from "@/lib/i18n";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

export default function Profile() {
  const { user, access, logout, lang, setLang } = useAuth();
  const t = (k) => tr(k, lang);
  const navigate = useNavigate();
  const [opening, setOpening] = useState(false);
  const [remindersEnabled, setRemindersEnabled] = useState(false);
  const [remindersSaving, setRemindersSaving] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleDeleteMyData = async () => {
    setDeleting(true);
    try {
      const r = await api.post("/user/delete-my-data");
      const c = r?.data?.counts || {};
      toast.success(
        lang === "en"
          ? `Deleted: ${c.scenes || 0} scenes, ${c.leo_chats || 0} chats, ${c.career_goals || 0} goals.`
          : `Usunięto: ${c.scenes || 0} scen, ${c.leo_chats || 0} rozmów, ${c.career_goals || 0} celów.`
      );
      setShowDeleteModal(false);
      // Bounce to dashboard — the app naturally re-fetches empty state.
      setTimeout(() => navigate("/dashboard"), 800);
    } catch (e) {
      toast.error(e?.response?.data?.detail || (lang === "en" ? "Delete failed" : "Nie udało się usunąć"));
    } finally {
      setDeleting(false);
    }
  };

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const r = await api.get("/reminders/settings");
        setRemindersEnabled(!!r.data?.enabled);
      } catch { /* keep default */ }
    })();
  }, [user]);

  if (!user) return null;

  const toggleReminders = async (next) => {
    setRemindersSaving(true);
    try {
      await api.patch("/reminders/settings", { enabled: !!next });
      setRemindersEnabled(!!next);
      toast.success(t("prof.reminders_saved"));
    } catch { toast.error(t("prof.reminders_fail")); }
    finally { setRemindersSaving(false); }
  };

  const openPortal = async () => {
    setOpening(true);
    try {
      const r = await api.post("/payments/portal", {
        return_url: `${window.location.origin}/profile`,
      });
      const url = r.data?.portal_url;
      if (url) {
        window.location.href = url;
      } else {
        toast.error(t("prof.manage_sub_fail"));
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("prof.manage_sub_fail"));
    } finally {
      setOpening(false);
    }
  };

  return (
    <div className="max-w-lg">
      <div className="mono text-[11px] tracking-[0.4em] text-amber-500 uppercase">{t("prof.title")}</div>
      <h1 className="serif text-5xl text-stone-100 mt-2">{user.name}</h1>

      <div className="paper p-6 mt-8 space-y-3">
        <div><span className="label-cap">{t("prof.email")}</span><div className="text-stone-200 mono text-sm mt-1">{user.email}</div></div>
        <div><span className="label-cap">{t("prof.created")}</span><div className="text-stone-200 mono text-sm mt-1">{new Date(user.created_at).toLocaleString(lang === "pl" ? "pl-PL" : "en-US")}</div></div>
        <div><span className="label-cap">{t("prof.provider")}</span><div className="text-stone-200 mono text-sm mt-1 uppercase">{user.provider}</div></div>
      </div>

      <div className="paper p-6 mt-4" data-testid="profile-reminders-block">
        <div className="label-cap flex items-center gap-2"><Bell size={12}/> {t("prof.reminders_title")}</div>
        <p className="text-stone-400 text-sm mt-2 leading-relaxed">{t("prof.reminders_hint")}</p>
        <div className="mt-4 flex gap-2">
          <button
            data-testid="profile-reminders-on"
            onClick={()=>toggleReminders(true)}
            disabled={remindersSaving}
            className={`ghost-btn !py-2 !px-4 ${remindersEnabled ? "!border-amber-500 !text-amber-400" : ""}`}
          >
            {t("prof.reminders_on")}
          </button>
          <button
            data-testid="profile-reminders-off"
            onClick={()=>toggleReminders(false)}
            disabled={remindersSaving}
            className={`ghost-btn !py-2 !px-4 ${!remindersEnabled ? "!border-amber-500 !text-amber-400" : ""}`}
          >
            {t("prof.reminders_off")}
          </button>
        </div>
      </div>

      <div className="paper p-6 mt-4">
        <div className="label-cap flex items-center gap-2"><Globe size={12}/> {t("prof.language")}</div>
        <div className="mt-3 flex gap-2">
          {["pl","en"].map(l => (
            <button
              key={l}
              data-testid={`profile-lang-${l}`}
              onClick={()=>setLang(l)}
              className={`ghost-btn !py-2 !px-4 ${lang === l ? "!border-amber-500 !text-amber-400" : ""}`}
            >
              <span className="mono tracking-widest">{l.toUpperCase()}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="paper p-6 mt-4">
        <div className="label-cap">{t("prof.plan_limits")}</div>
        <div className="serif text-3xl text-amber-400 mt-1 uppercase">{access?.plan}</div>
        <div className="mono text-xs text-stone-400 mt-1">
          {t("prof.used")}: {access?.scenes_used_free}/{access?.free_limit} · {t("prof.credits")}: {access?.scene_credits}
          {access?.plan_expires_at && <> · {t("prof.expires")}: {new Date(access.plan_expires_at).toLocaleString(lang === "pl" ? "pl-PL" : "en-US")}</>}
        </div>
        <div className="flex flex-wrap gap-2 mt-4">
          <Link to="/pricing" data-testid="profile-change-plan" className="ghost-btn inline-flex">
            {t("prof.change_plan")}
          </Link>
          <button
            data-testid="profile-manage-sub-btn"
            onClick={openPortal}
            disabled={opening}
            className="pill-btn inline-flex"
            title={t("prof.manage_sub_hint")}
          >
            {opening
              ? (<><RefreshCw size={14} className="animate-spin"/> {t("prof.manage_sub_opening")}</>)
              : (<><CreditCard size={14}/> {t("prof.manage_sub")}</>)}
          </button>
        </div>
        <div className="mono text-[10px] text-stone-500 mt-2">{t("prof.manage_sub_hint")}</div>
      </div>

      {access?.usage && access?.limits && (() => {
        const pl = lang === "pl";
        const u = access.usage, l = access.limits;
        const cps = u.career_path_slot_usage || {};
        const rows = [
          ["scenes", pl ? "Sceny" : "Scenes", `${u.scenes_used_period ?? 0} / ${l.scenes ?? "—"}`],
          ["variants", pl ? "Warianty" : "Variants", `${u.variants_used_period ?? 0} / ${l.variants ?? ((l.variants_per_scene ?? 0) * (l.scenes ?? 0))}`],
          ["reader", "AI Reader", `${Math.floor((u.reader_stt_seconds_used || 0) / 60)} min / ${Math.round((l.reader_stt_seconds || 0) / 60)} min`],
          ...(l.career_paths_ready != null
            ? [["cp-ready", pl ? "Ścieżki kariery (gotowe)" : "Career Paths (ready-made)", `${cps.ready ?? 0} / ${l.career_paths_ready}`],
               ["cp-generated", pl ? "Ścieżki kariery (generowane)" : "Career Paths (personalized)", `${cps.generated ?? 0} / ${l.career_paths_generated}`]]
            : [["cp", pl ? "Ścieżki kariery" : "Career Paths", `${u.career_paths_used_period ?? 0} / ${l.career_paths ?? "—"}`]]),
        ];
        return (
          <div className="paper p-6 mt-6" data-testid="profile-period-limits">
            <div className="label-cap">{pl ? "Twoje limity w tym okresie" : "Your limits this period"}</div>
            <div className="mt-3 space-y-1">
              {rows.map(([id, label, val]) => (
                <div key={id} className="mono text-xs text-stone-400 flex justify-between gap-4" data-testid={`profile-limit-${id}`}>
                  <span>{label}</span><span className="text-stone-200">{val}</span>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      <div className="paper p-6 mt-6" data-testid="profile-reminders-section">
        <div className="label-cap">{t("gr.title")}</div>
        <p className="text-stone-400 text-sm mt-2">{t("gr.tagline")}</p>
        <Link to="/reminders" data-testid="profile-reminders-link" className="ghost-btn mt-4 inline-flex">
          {t("gr.title")} →
        </Link>
      </div>

      {/* --- Privacy & Data — 3 informational tiles + destructive action --- */}
      <div className="paper p-6 mt-6" data-testid="profile-privacy-section">
        <div className="label-cap flex items-center gap-2">
          <Lock size={12} className="text-amber-400"/>
          {lang === "en" ? "Privacy & Data" : "Prywatność i Dane"}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
          <div className="rounded border border-stone-800 p-4 bg-stone-950/40" data-testid="privacy-tile-truth">
            <div className="text-base font-semibold text-stone-100 mb-2">
              {lang === "en" ? "🔒 Your truth is protected" : "🔒 Twoja prawda jest chroniona"}
            </div>
            <div className="text-sm text-stone-300 leading-relaxed">
              {lang === "en"
                ? "We use advanced encryption standards to protect the privacy of your creative process. Your content is stored in encrypted form and is not visible in standard administrative views."
                : "Stosujemy zaawansowane standardy szyfrowania, aby chronić poufność Twojego procesu twórczego. Twoje treści są przechowywane w bazie danych w zaszyfrowanej postaci i nie są widoczne w standardowych widokach administracyjnych."}
            </div>
          </div>
          <div className="rounded border border-stone-800 p-4 bg-stone-950/40" data-testid="privacy-tile-ownership">
            <div className="text-base font-semibold text-stone-100 mb-2">
              {lang === "en" ? "🫶 Your data belongs to you" : "🫶 Twoje dane należą do Ciebie"}
            </div>
            <div className="text-sm text-stone-300 leading-relaxed">
              {lang === "en"
                ? "Your content remains yours. Leo does not sell your data or use it for advertising. Access is limited to what is necessary for Leo to provide its features."
                : "Twoje treści pozostają Twoje. Leo ich nie sprzedaje ani nie wykorzystuje do celów reklamowych. Dostęp do nich jest ograniczony do sytuacji niezbędnych do działania Leo."}
            </div>
          </div>
          <div className="rounded border border-stone-800 p-4 bg-stone-950/40" data-testid="privacy-tile-ai">
            <div className="text-base font-semibold text-stone-100 mb-2">
              {lang === "en" ? "✨ AI works for you" : "✨ AI pracuje dla Ciebie"}
            </div>
            <div className="text-sm text-stone-300 leading-relaxed">
              {lang === "en"
                ? "Your content is shared with AI models only when needed to perform an analysis you choose. We do not use it for advertising profiles or for purposes unrelated to providing Leo’s features."
                : "Twoje treści są przekazywane do modeli AI tylko wtedy, gdy jest to potrzebne do wykonania wybranej przez Ciebie analizy. Nie wykorzystujemy ich do tworzenia profili reklamowych ani innych celów niezwiązanych z działaniem Leo."}
            </div>
          </div>
        </div>

        <div className="mt-6 pt-5 border-t border-stone-800/60">
          <button
            type="button"
            data-testid="privacy-delete-my-data-btn"
            onClick={() => setShowDeleteModal(true)}
            className="inline-flex items-center gap-2 px-4 py-2 rounded border border-red-900/50 bg-red-950/20 text-red-300 hover:bg-red-950/40 hover:border-red-800 transition text-sm"
          >
            <Trash2 size={14}/>
            {lang === "en" ? "Delete my data" : "Usuń trwale moje dane"}
          </button>
          <p className="text-[11px] text-stone-500 mt-2 leading-relaxed">
            {lang === "en"
              ? "Removes all scenes, analyses, Leo chats and Career Path goals. Your account, CRM contacts and settings stay."
              : "Usuwa wszystkie sceny, analizy, rozmowy z Leo i cele Ścieżki Kariery. Konto, kontakty CRM i ustawienia zostają."}
          </p>
        </div>
      </div>

      {/* --- Confirm modal — irreversible, verbatim copy from spec --- */}
      <Dialog open={showDeleteModal} onOpenChange={(v) => !deleting && setShowDeleteModal(v)}>
        <DialogContent className="bg-stone-950 border border-red-900/50 max-w-md" data-testid="delete-data-confirm-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-300">
              <AlertTriangle size={18}/>
              {lang === "en" ? "Delete my data" : "Usuń trwale moje dane"}
            </DialogTitle>
            <DialogDescription className="text-stone-300 text-sm leading-relaxed pt-3">
              {lang === "en"
                ? "Are you sure? This operation is irreversible. All your scenes, analyses, chat history with Leo, and Career Path goals will be permanently removed."
                : "Czy na pewno? Ta operacja jest nieodwracalna. Wszystkie Twoje sceny, analizy, historia rozmów z Leo oraz cele w Ścieżce Kariery zostaną bezpowrotnie usunięte."}
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center justify-end gap-2 mt-4">
            <button
              type="button"
              data-testid="delete-data-cancel-btn"
              onClick={() => setShowDeleteModal(false)}
              disabled={deleting}
              className="ghost-btn"
            >
              {lang === "en" ? "Cancel" : "Anuluj"}
            </button>
            <button
              type="button"
              data-testid="delete-data-confirm-btn"
              onClick={handleDeleteMyData}
              disabled={deleting}
              className="inline-flex items-center gap-2 px-4 py-2 rounded bg-red-700 hover:bg-red-600 text-white text-sm font-semibold disabled:opacity-60"
            >
              {deleting
                ? (lang === "en" ? "Deleting…" : "Usuwanie…")
                : (lang === "en" ? "Yes, delete permanently" : "Tak, usuń bezpowrotnie")}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {user.is_admin && (
        <div className="paper p-6 mt-6" data-testid="profile-admin-section">
          <div className="label-cap flex items-center gap-2"><ShieldCheck size={12} className="text-amber-400"/> {lang === "pl" ? "Administrator" : "Administrator"}</div>
          <p className="text-stone-400 text-sm mt-2">{lang === "pl"
            ? "Konfiguracja płatności Stripe (klucze Live / Test, Webhook Secret, Price IDs)."
            : "Stripe payments configuration (Live / Test keys, Webhook Secret, Price IDs)."}</p>
          <Link to="/admin/stripe" data-testid="profile-admin-stripe-link" className="pill-btn mt-4 inline-flex">
            <ShieldCheck size={14}/> {lang === "pl" ? "Konfiguracja Stripe" : "Stripe configuration"}
          </Link>
        </div>
      )}

      <button data-testid="profile-logout" onClick={()=>{logout(); navigate("/");}} className="pill-btn mt-6"><LogOut size={16}/> {t("nav.logout")}</button>
    </div>
  );
}
